import React, { useMemo, useState, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import { useTheme } from '@mui/material/styles';
import { debounce } from '../../utils/debounce';

interface Props {
  onChange: (search: string) => void;
  value?: string;
  placeholder?: string;
}

const SearchInput: React.FC<Props> = ({
  onChange,
  value,
  placeholder = 'Search...',
  ...props
}) => {
  const [internalValue, setInternalValue] = useState(value || '');
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  useEffect(() => {
    if (value !== undefined) {
      setInternalValue(value);
    }
  }, [value]);

  const onChangeDebounce = useMemo(
    () => debounce((search) => onChange(search), 500),
    [onChange]
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInternalValue(newValue);
    onChangeDebounce(newValue);
  };

  return (
    <TextField
      variant="outlined"
      placeholder={placeholder}
      value={internalValue}
      size="small"
      onChange={handleChange}
      fullWidth
      sx={{
        '& .MuiInputBase-root': {
          fontSize: { xs: '0.875rem', sm: '0.875rem', md: '0.8rem' },
          minHeight: { xs: 44 },
          borderRadius: { xs: '10px', sm: '12px' },
          transition: 'all 0.2s ease-in-out',
        },
        '& .MuiOutlinedInput-root': {
          backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.04)' : '#FFFFFF',
          border: '1px solid',
          borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.10)' : '#E5E7EB',
          borderRadius: { xs: '10px', sm: '12px' },
          transition: 'all 0.2s ease-in-out',
          '& fieldset': {
            border: 'none',
          },
          '&:hover': {
            backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : '#F9FAFB',
            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.18)' : '#D1D5DB',
          },
          '&.Mui-focused': {
            backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : '#FFFFFF',
            borderColor: '#7C3AED',
            boxShadow: isDarkMode
              ? '0 0 0 2px rgba(124, 58, 237, 0.30)'
              : '0 0 0 2px rgba(124, 58, 237, 0.20)',
          },
        },
        '& .MuiInputBase-input': {
          padding: { xs: '10px 12px', md: '8px 12px' },
          fontSize: { xs: '0.875rem', sm: '0.875rem', md: '0.8rem' },
          color: isDarkMode ? 'rgba(255, 255, 255, 0.75)' : '#111827',
          '&::placeholder': {
            color: isDarkMode ? 'rgba(255, 255, 255, 0.35)' : '#9CA3AF',
            opacity: 1,
          },
        },
        '& .MuiInputAdornment-root': {
          color: isDarkMode ? 'rgba(255, 255, 255, 0.75)' : '#111827',
          marginLeft: 1,
        },
        '& .MuiInputLabel-root.Mui-focused': {
          color: '#7C3AED'
        }
      }}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: (theme: any) => theme.typography.fontSizes.subheading }} />
            </InputAdornment>
          ),
        },
      }}
      {...props}
    />
  );
};

export default SearchInput;
